/**
 * iter-264 — Polling-based stem extraction client.
 *
 * Drop-in replacement for `streamMultipartSse` ON THE EXTRACTION step
 * (NOT on the upload step, which already direct-uploads to fal.media
 * since iter-260c). Same callable surface — same `{ url, formData,
 * signal, onEvent }` props, same final-event resolved value — so the
 * caller in `useStemExtraction.js` only has to swap the import.
 *
 * RCA from iter-264: nginx-ingress's proxy-read-timeout (60 s default)
 * does NOT reset on SSE comment lines. Our SSE keepalives kept the
 * TCP socket open but the proxy's "no real event" timer kept ticking,
 * killing connections at exactly the moment fal.ai was assembling its
 * final stem URLs. Result: user saw "fal.ai stopped at status
 * 'progress'" for ANY clip longer than ~45 s.
 *
 * The polling model splits a single 60-90 s HTTP request into ~30
 * short requests of <1 s each. NO proxy timeout (idle OR total) can
 * ever bite us.
 *
 * Wire shape (matches what `useStemExtraction.js` expects):
 *
 *   onEvent({status: "submitted", audio_url, model})
 *   onEvent({status: "queued",  position, request_id})    repeats while queued
 *   onEvent({status: "progress", logs?: [...], request_id}) repeats while in_progress
 *   onEvent({status: "done", stems: {name: url, ...}})    — exactly once
 *   OR throws Error("…")                                  — terminal failure
 *
 * Persists the in-flight request_id to localStorage so a page refresh
 * resumes the same job instead of starting (and PAYING for) a new one.
 */

const POLL_INTERVAL_MS = 3000;
// Hard cap on total polling duration as a safety net. fal-ai/demucs
// nominally finishes in 30-90 s but spike-load can push it to 5-6 min.
// We give 12 min before declaring the job dead. The user can always
// manually cancel via the toolbar.
const POLL_MAX_DURATION_MS = 12 * 60 * 1000;
const LS_KEY = "elpm.stem_extract.inflight";

/**
 * Persist or clear the in-flight request_id so a page refresh can
 * resume polling instead of starting a new (expensive) job.
 */
function _saveInflight(state) {
  try {
    if (state) localStorage.setItem(LS_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
    else localStorage.removeItem(LS_KEY);
  } catch (e) { void e; /* private mode → silently ignored */ }
}

export function readInflightExtraction() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire stored entries older than POLL_MAX_DURATION_MS so we
    // don't try to "resume" a job fal.ai has long since forgotten.
    if (Date.now() - (parsed.savedAt || 0) > POLL_MAX_DURATION_MS) {
      localStorage.removeItem(LS_KEY);
      return null;
    }
    return parsed;
  } catch (e) { void e; return null; }
}

export function clearInflightExtraction() { _saveInflight(null); }

/**
 * Promise-friendly sleep that respects an AbortSignal.
 * Rejects with `signal.reason` if aborted before resolution.
 */
function _abortableSleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason || new DOMException("Aborted", "AbortError"));
    const t = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(signal.reason || new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort);
  });
}

/**
 * Best-effort cancel — fire-and-forget, doesn't block the abort path.
 * Uses `keepalive: true` so it survives even if the tab is being
 * torn down at the moment of cancellation.
 */
function _cancelJob(apiBase, requestId) {
  if (!apiBase || !requestId) return;
  try {
    fetch(`${apiBase}/api/stems/extract_async/cancel/${requestId}`, {
      method: "POST",
      keepalive: true,
    }).catch(() => {});
  } catch (e) { void e; }
}

/**
 * Main entry point — kicks off a Demucs job + polls it to completion.
 *
 * @param {Object} args
 * @param {string} args.url        Full URL to POST /api/stems/extract_async (kept for parity with streamMultipartSse).
 * @param {FormData} args.formData Required `url` field (public audio URL) + optional `model` field.
 * @param {AbortSignal} args.signal Caller's cancellation handle.
 * @param {Function} args.onEvent  Called for each status snapshot.
 * @returns {Promise<Object>}      Resolves with the FINAL event ({status:"done", stems:{...}}).
 */
export async function streamPollingExtraction({ url, formData, signal, onEvent }) {
  const apiBase = url.replace(/\/api\/stems\/extract_async\/?$/, "");
  const audioUrl = formData.get("url");
  const model = formData.get("model") || "htdemucs_6s";
  if (!audioUrl) throw new Error("streamPollingExtraction: formData must include a `url` field");

  // ── Step 1: submit ─────────────────────────────────────────────────
  const submitRes = await fetch(`${apiBase}/api/stems/extract_async`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url: audioUrl, model }),
    signal,
  });
  if (!submitRes.ok) {
    const txt = await submitRes.text().catch(() => "");
    let detail = txt;
    try {
      const p = JSON.parse(txt);
      if (p?.detail?.error) detail = p.detail.error;
      else if (p?.detail) detail = typeof p.detail === "string" ? p.detail : JSON.stringify(p.detail);
    } catch (e) { void e; }
    const err = new Error(`HTTP ${submitRes.status}: ${String(detail).slice(0, 240)}`);
    err.status = submitRes.status;
    throw err;
  }
  const submitJson = await submitRes.json();
  const requestId = submitJson.request_id;
  if (!requestId) throw new Error("Stem extraction submit returned no request_id");
  _saveInflight({ requestId, audioUrl, model, apiBase });

  // Emit a "submitted" event so the existing progress UI flips to the
  // "fal.ai accepted the job" phase immediately.
  onEvent?.({ status: "submitted", audio_url: audioUrl, model, request_id: requestId });

  // Delegate to the shared polling loop.
  return await pollExtractionToCompletion({ apiBase, requestId, signal, onEvent });
}

/**
 * iter-264 — Standalone polling loop exposed so callers can RESUME a
 * job that was submitted in a previous tab session. Used by
 * `useStemExtraction.js` when it finds a non-expired in-flight
 * request_id in `localStorage` on mount.
 *
 * Identical contract to `streamPollingExtraction` but skips the submit
 * step — you supply the existing `requestId` directly. The function
 * still wires AbortSignal → /cancel/{id}, throttles polls to
 * POLL_INTERVAL_MS, and clears the localStorage entry on terminal
 * success / error.
 */
export async function pollExtractionToCompletion({ apiBase, requestId, signal, onEvent }) {
  if (!apiBase) throw new Error("pollExtractionToCompletion: apiBase is required");
  if (!requestId) throw new Error("pollExtractionToCompletion: requestId is required");

  // Wire cancellation: when the caller aborts we MUST best-effort
  // cancel the fal.ai job to stop wasting credits.
  const cancelOnAbort = () => _cancelJob(apiBase, requestId);
  signal?.addEventListener("abort", cancelOnAbort, { once: true });

  const startedAt = Date.now();
  try {
    while (true) {
      if (signal?.aborted) throw signal.reason || new DOMException("Aborted", "AbortError");
      if (Date.now() - startedAt > POLL_MAX_DURATION_MS) {
        _cancelJob(apiBase, requestId);
        throw new Error(`Stem extraction polling exceeded ${Math.round(POLL_MAX_DURATION_MS / 60000)} min — fal.ai may be overloaded. Try again in a few minutes.`);
      }

      const statusRes = await fetch(
        `${apiBase}/api/stems/extract_async/status/${encodeURIComponent(requestId)}`,
        { signal },
      );
      if (!statusRes.ok) {
        // A 4xx/5xx on the status endpoint is unusual — surface it and
        // stop polling. The /status route always returns 200 with a
        // structured `{status:"error",error:…}` for fal-side failures.
        const txt = await statusRes.text().catch(() => "");
        throw new Error(`Status poll HTTP ${statusRes.status}: ${txt.slice(0, 200)}`);
      }
      const ev = await statusRes.json();

      // Normalise field name so the existing useStemExtraction UI keeps
      // working: it expects `status: "queued"|"progress"|"done"`. Our
      // backend returns `in_progress` — map it to `progress` for parity.
      const normalised = ev.status === "in_progress" ? { ...ev, status: "progress" } : ev;
      // Tag with the request_id so the UI can render a "Job ID:
      // <id>" hint for debugging / customer support.
      normalised.request_id = requestId;

      onEvent?.(normalised);

      if (normalised.status === "done") {
        _saveInflight(null);
        return normalised;
      }
      if (normalised.status === "error") {
        _saveInflight(null);
        const err = new Error(normalised.error || "fal.ai job failed");
        if (normalised.code) err.code = normalised.code;
        throw err;
      }

      // Still queued or in progress — wait before the next poll.
      await _abortableSleep(POLL_INTERVAL_MS, signal);
    }
  } catch (e) {
    if (e?.name === "AbortError") _saveInflight(null);
    throw e;
  } finally {
    signal?.removeEventListener("abort", cancelOnAbort);
  }
}

// Test-only — exposed so tests can override the poll interval. Production
// code should NOT touch this; the 3-second default is tuned to balance
// responsiveness with fal.ai API rate limits.
export const _testHooks = {
  setPollInterval: () => {
    throw new Error("Override POLL_INTERVAL_MS via jest.mock instead — production code is immutable.");
  },
};
